import { flintShapes, formatMm, shapeBounds } from '@bw/core';
import type { BuildingModel, PlacedWall } from './types.js';

/**
 * The building in words (phase 2, M8).
 *
 * A canvas says nothing to a screen reader, and a WebGL canvas says less than
 * a 2D one — there is not even a DOM to inspect. So the same building is also
 * a structured description, carrying forward the pattern the 2D editor already
 * uses for its wall outline.
 *
 * Built here rather than in the viewer so it can be tested without a GPU, and
 * so it stays true whether or not the 3D view was ever opened.
 */

export interface BuildingDescription {
  readonly summary: string;
  readonly walls: readonly WallDescription[];
  readonly roof: string;
  readonly warnings: readonly string[];
}

export interface WallDescription {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  readonly openings: readonly string[];
  /** Parts of the wall built in something other than brick. */
  readonly panels: readonly string[];
}

const metres = (mm: number): string => (mm / 1000).toFixed(2);

/** Compass-ish bearing, so a wall can be named by where it faces. */
function facing(rotationDeg: number): string {
  const normalised = ((rotationDeg % 360) + 360) % 360;
  const names = ['south', 'east', 'north', 'west'];
  const index = Math.round(normalised / 90) % 4;
  // Only claim a direction when the wall is square to the grid; anything else
  // would be a guess dressed up as a fact.
  return Math.abs(normalised - index * 90) < 1 ? (names[index] as string) : 'at an angle';
}

function describeWall(building: BuildingModel, wall: PlacedWall): WallDescription {
  const bounds = wall.source.model.bounds();
  const length = wall.extent === null ? 0 : wall.extent.maxX - wall.extent.minX;
  const height = bounds === null ? 0 : bounds.maxY - Math.min(0, bounds.minY);
  const parts = [
    wall.brickCount === 0
      ? 'no bricks laid'
      : `${metres(length)} by ${metres(height)} metres, ${wall.brickCount} bricks`,
    `${formatMm(wall.placement.thicknessMm)} mm thick`,
    `facing ${facing(wall.placement.rotationDeg)}`,
    `at X ${formatMm(wall.placement.origin.x)}, Z ${formatMm(wall.placement.origin.z)} mm`,
  ];

  const openings = building.openings
    .filter((opening) => opening.wallId === wall.id)
    .map((opening) => {
      const size = `${formatMm(opening.width)} by ${formatMm(opening.height)} mm`;
      const sill = `sill at ${formatMm(opening.y)} mm`;
      const joinery = opening.rectangular
        ? 'with a frame'
        : 'an opening with no frame, because its outline is not rectangular';
      return `${opening.role === 'door' ? 'Door' : 'Window'}, ${size}, ${sill}, ${joinery}`;
    });

  // Flint is not an opening and does not belong in that list, but it is the
  // one thing about a wall that a reader cannot get from the brick count:
  // several square metres of it are simply missing from the tally.
  const panels = flintShapes(wall.source.model.sketch).map((shape) => {
    const box = shapeBounds(shape);
    return [
      `Flint, ${formatMm(box.maxX - box.minX)} by ${formatMm(box.maxY - box.minY)} mm`,
      `from ${formatMm(box.minY)} mm up`,
    ].join(', ');
  });

  return { id: wall.id, name: wall.name, detail: parts.join(', '), openings, panels };
}

export function describeBuilding(building: BuildingModel): BuildingDescription {
  const walls = building.walls.map((wall) => describeWall(building, wall));
  const openings = building.openings.length;
  const footprint = building.footprint;

  const flint = walls.reduce((count, wall) => count + wall.panels.length, 0);

  const summary = [
    `${building.walls.length} ${building.walls.length === 1 ? 'wall' : 'walls'}`,
    `${building.brickCount} bricks`,
    ...(flint === 0 ? [] : [`${flint} flint ${flint === 1 ? 'panel' : 'panels'}`]),
    openings === 0 ? 'no openings' : `${openings} ${openings === 1 ? 'opening' : 'openings'}`,
    footprint.closed
      ? `a closed outline of ${(footprint.areaMm2 / 1_000_000).toFixed(2)} square metres`
      : 'an outline that does not close',
  ].join(', ');

  const roof = describeRoof(building);

  const warnings: string[] = [];
  if (!footprint.closed) {
    warnings.push(
      footprint.openEnds.length === 0
        ? 'The walls form more than one loop, so there is no single outline to roof.'
        : `${footprint.openEnds.length} wall ends join nothing, so the outline does not close.`,
    );
  }
  if (building.roof.fallback) {
    warnings.push(
      'This outline could not be hipped, so the roof is a gable over its bounding box.',
    );
  }
  if (building.overlaps.length > 0) {
    warnings.push(
      `${building.overlaps.length} corners where two walls share the same space. Walls are placed exactly as drawn, so those overlaps are left as they are.`,
    );
  }
  for (const opening of building.openings) {
    if (opening.rectangular) continue;
    const wall = building.walls.find((candidate) => candidate.id === opening.wallId);
    warnings.push(
      `An opening on ${wall?.name ?? 'a wall'} is not rectangular, so it carries no frame.`,
    );
  }

  return { summary, walls, roof, warnings };
}

function describeRoof(building: BuildingModel): string {
  const roof = building.roof;
  if (roof.style === 'none' || roof.faces.length === 0) return 'No roof.';
  const rise = roof.apexY - roof.eavesY;
  return [
    `${roof.style === 'hip' ? 'Hip' : 'Gable'} roof in ${roof.faces.length} slopes`,
    `eaves at ${metres(roof.eavesY)} metres`,
    `ridge at ${metres(roof.apexY)} metres`,
    `rising ${metres(rise)} metres`,
  ].join(', ');
}
